import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import '@/styles/ExecutiveDashStyles.scss';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList, Cell, LineChart, Line, Legend } from 'recharts';
import { computeSharepointCollabAggregate } from '@/utils/scoring';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const ONE_GB_MB = 1024;
const spaceNumber = (n, fractionDigits = 2) => {
  if (n === null || n === undefined || isNaN(n)) return '0';
  const parts = Number(n).toFixed(fractionDigits).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts[1] && parseInt(parts[1],10) !== 0 ? parts.join('.') : parts[0];
};
const spaceInteger = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '0';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};
const pctFmt = (v, fd=1) => (v==null || isNaN(v) ? '-' : `${Number(v).toFixed(fd)}%`);

const COLORS = [
  '#1976d2','#26a69a','#7cb342','#ef6c00','#ab47bc','#ec407a','#d4e157','#8d6e63',
  '#ffa726','#42a5f5','#66bb6a','#26c6da','#ff7043','#5c6bc0','#9ccc65','#26a69a'
];

const SharepointSiteUsage = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode(); // Get print mode from context
  const [sites, setSites] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [sitesRes, summaryRes, activityRes, healthRes] = await Promise.all([
          apiFetch('/api/sharepoint/sites-usage'),
          apiFetch('/api/sharepoint/sites-usage/summary'),
          apiFetch('/api/sharepoint/sites-usage/activity?period=D90&inactiveThresholdDays=90'),
          apiFetch('/api/sharepoint/health-metrics')
        ]);
        if (!sitesRes.ok) throw new Error('Failed to load site list');
        if (!summaryRes.ok) throw new Error('Failed to load summary');
        if (!activityRes.ok) throw new Error('Failed to load activity');
        const [sitesJson, summaryJson, activityJson, healthJson] = await Promise.all([
          sitesRes.json(), summaryRes.json(), activityRes.json(), healthRes.ok ? healthRes.json() : Promise.resolve(null)
        ]);
        if (isMounted) {
          setSites(sitesJson);
          setSummary(summaryJson);
          setActivity(activityJson);
          setHealth(healthJson);
        }
      } catch (e) {
          Sentry.captureException(e);
        if (isMounted) setError(e.message);
      } finally { if (isMounted) setLoading(false); }
    }
    load();
    return () => { isMounted = false; };
  }, []); // eslint-disable-line

  // Filter for chart display - only show sites >= 1GB for clarity
  const barsData = useMemo(() => {
    const filtered = (sites || []).filter(s => (s.storageUsedMB || 0) >= ONE_GB_MB);
    const mapped = filtered.map(s => ({ name: s.siteName || s.url, gb: (s.storageUsedMB || 0) / ONE_GB_MB }));
    mapped.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return mapped;
  }, [sites]);

  const xDomainMaxGB = useMemo(() => {
    const max = barsData.reduce((m, x) => Math.max(m, x.gb || 0), 0);
    return Math.ceil(max * 1.08);
  }, [barsData]);

  const colorMap = useMemo(() => {
    const map = new Map();
    barsData.forEach((p, i) => map.set(p.name, COLORS[i % COLORS.length]));
    return map;
  }, [barsData]);

  const yAxisWidth = useMemo(() => {
    const maxLen = barsData.reduce((m, x) => Math.max(m, (x.name || '').length), 0);
    return Math.max(100, Math.min(220, Math.round(maxLen * 5.5)));
  }, [barsData]);

  // Dynamic chart height based on number of bars (16px per bar, min 200, max 550)
  // In print mode, constrain to fixed height for A4 layout
  const barChartHeight = useMemo(() => {
    if (isPrintMode) {
      // Fixed height for print: constrain to fit on single page with surrounding content
      return Math.min(400, Math.max(250, barsData.length * 12 + 40));
    }
    return Math.max(200, Math.min(550, barsData.length * 16 + 40));
  }, [barsData, isPrintMode]);

  const renderGBLabel = (props) => {
    const { x, y, width, height, value } = props;
    const posX = x + width + 4;
    const posY = y + height / 2 + 3;
    return (
      <text x={posX} y={posY} fill="#444" fontSize={9} textAnchor="start">{`${spaceNumber(value)} GB`}</text>
    );
  };

  const totalSites = summary?.totalSiteCount || 0;
  const totalStorageMB = summary?.totalStorageMB || 0;
  const totalStorageGB = totalStorageMB / ONE_GB_MB;
  const sitesOver1GB = summary?.sitesOver1GB || 0;
  const storageOver1GBGB = (summary?.storageOver1GBMB || 0) / ONE_GB_MB;

  const activePrevPct = health?.activePrevMonthPct ?? health?.ActivePrevMonthPct;
  const activePriorPct = health?.activePriorMonthPct ?? health?.ActivePriorMonthPct;
  const headroomPct = health?.capacityHeadroomPct ?? health?.CapacityHeadroomPct;
  const growthPct = health?.storageGrowthPct ?? health?.StorageGrowthPct;
  const growthMB = health?.storageGrowthMB ?? health?.StorageGrowthMB;
  const bucketLast7 = health?.bucketLast7 ?? health?.BucketLast7;
  const bucket8to30 = health?.bucket8To30 ?? health?.Bucket8To30;
  const bucket31to90 = health?.bucket31To90 ?? health?.Bucket31To90;
  const bucketOver90 = health?.bucketOver90 ?? health?.BucketOver90;
  const totalSiteCount = health?.totalSiteCount ?? health?.TotalSiteCount;
  const dataWindowDays = health?.dataWindowDays ?? health?.DataWindowDays;
  const capacityReported = health?.capacityReported ?? health?.CapacityReported;
  const limitedData = health?.limitedDataCoverage ?? health?.LimitedDataCoverage;

  const activeDelta = activePrevPct!=null && activePriorPct!=null ? activePrevPct - activePriorPct : null;
  const { score: collabScore, growthProfileScore } = computeSharepointCollabAggregate(activePrevPct, headroomPct, growthPct);

  const bucketPct = (count) => (totalSiteCount ? (count/totalSiteCount)*100 : null);

  // Friendly summary numbers for recency section
  const usedLast30Count = (bucketLast7||0) + (bucket8to30||0);
  const usedLast30Pct = bucketPct(usedLast30Count);
  const inactive3PlusCount = bucketOver90 || 0;
  const inactive3PlusPct = bucketPct(inactive3PlusCount);

  const rawStorageTrend = (activity?.tenantStorageTrend || []).map(p => ({ date: new Date(p.date), usedGB: p.storageUsedMB / ONE_GB_MB, allocatedGB: p.storageAllocatedMB / ONE_GB_MB }));

  const { prevMonthLabel, priorMonthLabel, nextMonthLabel, mergedMonthData, daysPriorMonth } = useMemo(() => {
    const now = new Date();
    const firstThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const firstPrev = new Date(firstThisMonth.getFullYear(), firstThisMonth.getMonth() - 1, 1);
    const endPrevExclusive = new Date(firstThisMonth);
    const firstBeforePrev = new Date(firstPrev.getFullYear(), firstPrev.getMonth() - 1, 1);
    const endBeforePrevExclusive = new Date(firstPrev);
    const seriesPrev = rawStorageTrend.filter(p => p.date >= firstPrev && p.date < endPrevExclusive)
      .map(p => ({ day: p.date.getDate(), usedGB: p.usedGB }));
    const seriesPrior = rawStorageTrend.filter(p => p.date >= firstBeforePrev && p.date < endBeforePrevExclusive)
      .map(p => ({ day: p.date.getDate(), usedGB: p.usedGB }));
    const daysPrev = new Date(firstPrev.getFullYear(), firstPrev.getMonth() + 1, 0).getDate();
    const daysPrior = new Date(firstBeforePrev.getFullYear(), firstBeforePrev.getMonth() + 1, 0).getDate();
    const maxDays = Math.max(daysPrev, daysPrior);
    const mapPrev = new Map(seriesPrev.map(x => [x.day, x.usedGB]));
    const mapPrior = new Map(seriesPrior.map(x => [x.day, x.usedGB]));
    const merged = Array.from({ length: maxDays }, (_, i) => ({ day: i + 1, prevUsed: mapPrev.get(i+1) ?? null, priorUsed: mapPrior.get(i+1) ?? null }));
    const monthFmt = { month: 'long', year: 'numeric' };
    // nextMonthDate intentionally uses month + 1 to represent the first day of the next month.
    // JavaScript Date handles year rollover automatically (e.g. December → January).
    const nextMonthDate = new Date(firstThisMonth.getFullYear(), firstThisMonth.getMonth() + 1, 1);
    return { prevMonthLabel: firstPrev.toLocaleDateString(undefined, monthFmt), priorMonthLabel: firstBeforePrev.toLocaleDateString(undefined, monthFmt), nextMonthLabel: nextMonthDate.toLocaleDateString(undefined, monthFmt), mergedMonthData: merged, daysPriorMonth: daysPrior };
  }, [rawStorageTrend]);

  // Project next month's forecast using both months' data for a more accurate regression
  const { trendChartData, yDomain } = useMemo(() => {
    // Build a continuous timeline: prior month days 1..N map to x=1..N,
    // current month days 1..M map to x=N+1..N+M
    const regressionPoints = [];
    mergedMonthData.forEach(d => {
      if (d.priorUsed != null) regressionPoints.push({ x: d.day, y: d.priorUsed });
      if (d.prevUsed != null) regressionPoints.push({ x: daysPriorMonth + d.day, y: d.prevUsed });
    });
    let slope = 0, intercept = 0, hasTrend = false;
    if (regressionPoints.length >= 2) {
      const n = regressionPoints.length;
      const sumX = regressionPoints.reduce((a, p) => a + p.x, 0);
      const sumY = regressionPoints.reduce((a, p) => a + p.y, 0);
      const sumXY = regressionPoints.reduce((a, p) => a + p.x * p.y, 0);
      const sumX2 = regressionPoints.reduce((a, p) => a + p.x * p.x, 0);
      const denom = n * sumX2 - sumX * sumX;
      if (denom !== 0) {
        slope = (n * sumXY - sumX * sumY) / denom;
        intercept = (sumY - slope * sumX) / n;
        hasTrend = true;
      }
    }
    // Forecast: next month starts at x = daysPrior + daysPrev + day
    const lastPrevDay = mergedMonthData.filter(d => d.prevUsed != null).length;
    const forecastOffset = daysPriorMonth + lastPrevDay;
    const data = mergedMonthData.map(d => ({
      ...d,
      predictedGB: hasTrend ? intercept + slope * (forecastOffset + d.day) : null
    }));
    const allValues = data.flatMap(d => [d.prevUsed, d.priorUsed, d.predictedGB]).filter(v => v != null);
    const minVal = allValues.length > 0 ? Math.min(...allValues) : 0;
    const maxVal = allValues.length > 0 ? Math.max(...allValues) : 100;
    const yMin = Math.max(0, Math.floor(minVal - 100));
    const yMax = Math.ceil(maxVal + 50);
    return { trendChartData: data, yDomain: [yMin, yMax] };
  }, [mergedMonthData, daysPriorMonth]);

  // Calculate when sites were LAST active (most recent activity date)
  const lastActivityInfo = useMemo(() => {
    if (!activity?.sites || activity.sites.length === 0) return null;
    const sitesWithDates = activity.sites.filter(s => s.lastActivityDate != null);
    if (sitesWithDates.length === 0) return null;
    const mostRecent = sitesWithDates.reduce((latest, site) => {
      const siteDate = new Date(site.lastActivityDate);
      return !latest || siteDate > new Date(latest.lastActivityDate) ? site : latest;
    }, null);
    if (!mostRecent) return null;
    const lastDate = new Date(mostRecent.lastActivityDate);
    const now = new Date();
    const monthsAgo = (now.getFullYear() - lastDate.getFullYear()) * 12 + (now.getMonth() - lastDate.getMonth());
    return {
      date: lastDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      monthsAgo,
      siteName: mostRecent.siteName || mostRecent.siteUrl
    };
  }, [activity]);

  if (loading) return <HexagonLoader />;
  if (error) return <div className="error-message" style={{ color: 'red' }}>{error}</div>;

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 80) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 60) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 40) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 20) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const scoreInterp = getScoreInterpretation(collabScore);
  const criticalAdoptionIssue = activePrevPct != null && activePrevPct === 0 && totalSites > 0 && lastActivityInfo != null;

  return (
    <div className="dashboard-card group-wrapper sharepoint-site-usage">
      <h2 style={{ marginTop: 0 }}>SharePoint Collaboration & Storage</h2>

      {/* Critical Adoption Warning - show prominently if 0% active */}
      {criticalAdoptionIssue && (
        <div style={{ margin: '0 0 16px 0', padding: 16, background: '#fff3cd', borderLeft: '4px solid #ff5722', borderRadius: 4 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ fontSize: 32, color: '#ff5722', lineHeight: 1 }}>⚠</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#d32f2f', marginBottom: 6 }}>Critical: SharePoint Adoption Issue Detected</div>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: '#444' }}>
                <strong>No SharePoint activity detected in {prevMonthLabel}.</strong> Last recorded activity was <strong>{lastActivityInfo.monthsAgo} month{lastActivityInfo.monthsAgo > 1 ? 's' : ''} ago ({lastActivityInfo.date})</strong> on site "{lastActivityInfo.siteName}".
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <strong>This suggests:</strong>
                  <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                    <li>Users may not know SharePoint exists or how to access it</li>
                    <li>Alternative collaboration tools (file shares, email) being used instead</li>
                    <li>Possible migration/onboarding incomplete</li>
                    <li>Wasted Microsoft 365 licensing costs (paying for unused SharePoint)</li>
                  </ul>
                </div>
                <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 4, fontSize: 13 }}>
                  <strong>Recommended Actions:</strong> Survey users, provide training, migrate key file shares to SharePoint, communicate benefits, or reassess licensing.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Executive Summary Banner */}
      {summary && (
        <>
          <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
            <div className="exec-banner-layout">
              <div className="exec-banner-score">
                <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
                  {collabScore != null ? Math.round(collabScore) : '–'}
                  <span className="exec-banner-suffix">/ 100</span>
                </div>
                <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
                <div className="exec-banner-sublabel">Collaboration Health Score</div>
              </div>
              <div className="exec-banner-body">
                <div className="exec-banner-text">
                  <strong>What this means:</strong> Your SharePoint environment has <strong>{totalSites} team sites</strong> using <strong>{spaceNumber(totalStorageGB)} GB</strong> of storage.
                  {activePrevPct != null && activePrevPct === 0 && (
                    <><strong> NO sites were actively used in {prevMonthLabel}</strong>{lastActivityInfo && <> - last activity was {lastActivityInfo.monthsAgo} month{lastActivityInfo.monthsAgo > 1 ? 's' : ''} ago</>}.</>
                  )}
                  {activePrevPct != null && activePrevPct > 0 && (
                    <> <strong>{activePrevPct.toFixed(0)}%</strong> of sites were actively used in {prevMonthLabel}{activeDelta != null && activeDelta !== 0 && <span style={{ color: activeDelta > 0 ? 'green' : '#d32f2f' }}> ({activeDelta > 0 ? '↑' : '↓'} {Math.abs(activeDelta).toFixed(1)}% from prior month)</span>}.</>
                  )}
                  {headroomPct != null && headroomPct < 20 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Storage capacity is running low ({headroomPct.toFixed(0)}% remaining).</span>}
                  {growthPct != null && Math.abs(growthPct) > 10 && <span style={{ color: '#ff5722', fontWeight: 600 }}> Storage {growthPct > 0 ? 'increased' : 'decreased'} by {Math.abs(growthPct).toFixed(1)}% last month.</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="metrics-row">
            <div className="metric-card" title="Total number of SharePoint team sites in your organization">
              <div className="metric-label">Total Sites</div>
              <div className="metric-value">{spaceInteger(totalSites)}</div>
            </div>
            <div className="metric-card" title="Number of sites storing at least 1 GB of content">
              <div className="metric-label">Sites with Significant Storage</div>
              <div className="metric-value">{spaceInteger(sitesOver1GB)}</div>
              <div className="metric-subtitle">(≥1 GB each)</div>
            </div>
            <div className="metric-card" title="Total storage used by all SharePoint sites">
              <div className="metric-label">Total Storage Used</div>
              <div className="metric-value">{spaceNumber(totalStorageGB, 1)} GB</div>
            </div>
            <div className="metric-card" title="Storage used by sites with at least 1 GB">
              <div className="metric-label">Storage (Significant Sites)</div>
              <div className="metric-value">{spaceNumber(storageOver1GBGB, 1)} GB</div>
            </div>
            <div className="metric-card" title="Average storage per site across all sites">
              <div className="metric-label">Average per Site</div>
              <div className="metric-value">{spaceNumber((summary.averageStorageMB || 0) / ONE_GB_MB, 1)} GB</div>
            </div>
            <div className="metric-card" title="Half of sites use less than this, half use more">
              <div className="metric-label">Median Storage</div>
              <div className="metric-value">{spaceNumber((summary.medianStorageMB || 0) / ONE_GB_MB, 1)} GB</div>
            </div>
          </div>

          {/* Activity & Health Metrics */}
          {health && (
            <>
              <h3 style={{ margin: '24px 0 12px 0', fontSize: 16, fontWeight: 600 }}>Usage & Capacity Insights</h3>
              <div className="metrics-row">
                <div className="metric-card" title={`Percentage of sites that had activity (views, edits, or users) in ${prevMonthLabel}`}>
                  <div className="metric-label">Active Sites ({prevMonthLabel})</div>
                  <div className="metric-value" style={{ color: activePrevPct != null && activePrevPct >= 50 ? '#2e7d32' : activePrevPct != null && activePrevPct >= 30 ? '#ffa000' : '#d32f2f' }}>{pctFmt(activePrevPct, 0)}</div>
                  {activeDelta != null && <div className="metric-subtitle" style={{ color: activeDelta >= 0 ? 'green' : '#d32f2f' }}>{activeDelta >= 0 ? '▲' : '▼'} {Math.abs(activeDelta).toFixed(1)}% vs prior month</div>}
                  {activePrevPct === 0 && lastActivityInfo && (
                    <div className="metric-subtitle" style={{ color: '#d32f2f', fontWeight: 600 }}>
                      Last active: {lastActivityInfo.monthsAgo}mo ago
                    </div>
                  )}
                </div>
                
                <div className="metric-card" title="How much storage capacity remains before hitting tenant limits">
                  <div className="metric-label">Capacity Remaining</div>
                  <div className="metric-value" style={{ color: headroomPct != null && headroomPct >= 30 ? '#2e7d32' : headroomPct != null && headroomPct >= 15 ? '#ffa000' : '#d32f2f' }}>{headroomPct != null ? pctFmt(headroomPct, 0) : capacityReported === false ? 'Unlimited' : '–'}</div>
                  {!capacityReported && headroomPct == null && <div className="metric-subtitle">No tenant limit set</div>}
                </div>

                <div className="metric-card" title="Month-over-month change in total storage. Healthy growth is typically 0–5% per month">
                  <div className="metric-label">Storage Growth</div>
                  <div className="metric-value" style={{ color: growthPct != null && Math.abs(growthPct) <= 5 ? '#2e7d32' : growthPct != null && Math.abs(growthPct) <= 10 ? '#ffa000' : '#d32f2f' }}>{growthPct != null ? (growthPct >= 0 ? '+' : '') + growthPct.toFixed(1) + '%' : '–'}</div>
                  {growthMB != null && <div className="metric-subtitle" style={{ color: growthMB >= 0 ? '#2e7d32' : '#d32f2f' }}>{growthMB >= 0 ? '▲' : '▼'} {spaceNumber(Math.abs(growthMB / ONE_GB_MB), 2)} GB vs prior month</div>}
                  {growthMB == null && <div className="metric-subtitle">vs prior month</div>}
                </div>

                {growthProfileScore != null && (
                  <div className="metric-card" title="Assessment of storage growth health: 0-5% growth scores 100/100 (ideal sustainable growth)">
                    <div className="metric-label">Growth Health Score</div>
                    <div className="metric-value" style={{ color: growthProfileScore >= 80 ? '#2e7d32' : growthProfileScore >= 60 ? '#ffa000' : '#d32f2f' }}>{Math.round(growthProfileScore)}/100</div>
                    <div className="metric-subtitle">
                      {growthProfileScore >= 80 ? 'Healthy growth' : growthProfileScore >= 60 ? 'Moderate growth' : 'Rapid growth'}
                    </div>
                  </div>
                )}

                {/* Simplified Activity Recency */}
                <div className="metric-card sharepoint-recency-card" style={{ flex: '1 1 440px', minWidth: 340 }} title="Shows how recently each site was used (views, edits, or users). Helps identify stale / archive candidates.">
                  <div className="metric-label" style={{ marginBottom: 8 }}>When Sites Were Last Used</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
                    <span title="Sites with activity in last 7 days" style={{ background: '#2e7d3215', padding: '6px 10px', borderRadius: 4, fontWeight: 600 }}>
                      Used last week: {bucketLast7 ?? '–'} <span style={{ color: '#666' }}>({pctFmt(bucketPct(bucketLast7), 0)})</span>
                    </span>
                    <span title="Activity 8–30 days ago" style={{ background: '#7cb34215', padding: '6px 10px', borderRadius: 4, fontWeight: 600 }}>
                      Used this month: {bucket8to30 ?? '–'} <span style={{ color: '#666' }}>({pctFmt(bucketPct(bucket8to30), 0)})</span>
                    </span>
                    <span title="Activity 31–90 days ago" style={{ background: '#ffa00015', padding: '6px 10px', borderRadius: 4, fontWeight: 600 }}>
                      Used in last 3 mo: {bucket31to90 ?? '–'} <span style={{ color: '#666' }}>({pctFmt(bucketPct(bucket31to90), 0)})</span>
                    </span>
                    <span title="No activity for 90+ days (review/cleanup)" style={{ background: '#d32f2f15', padding: '6px 10px', borderRadius: 4, fontWeight: 600 }}>
                      Inactive 3+ mo: {bucketOver90 ?? '–'} <span style={{ color: '#666' }}>({pctFmt(bucketPct(bucketOver90), 0)})</span>
                    </span>
                  </div>
                  <div style={{ fontSize: 11, marginTop: 8, color: '#444', lineHeight: 1.4 }}>
                    <strong>{usedLast30Count}</strong> of {totalSiteCount} sites ({pctFmt(usedLast30Pct,0)}) were used in the last 30 days. {inactive3PlusCount > 0 && (<><strong>{inactive3PlusCount}</strong> ({pctFmt(inactive3PlusPct,0)}) have been inactive for 3+ months – consider archiving or cleanup.</>)}
                  </div>
                  {dataWindowDays > 0 && (
                    <div style={{ fontSize: 10, marginTop: 6, color: '#666' }}>
                      Based on {dataWindowDays} days of usage data{limitedData ? ' (limited history – some counts may read 0 until more data accumulates)' : ''}.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Storage Distribution Chart */}
      <div className="dashboard-card sharepoint-chart-card">
        <h4 className="chart-title" style={{ fontSize: 16 }}>Storage by Site (Sites with ≥1 GB)</h4>
        <div className="chart-subtitle">
          Showing {barsData.length} sites. {totalSites - barsData.length > 0 && `${totalSites - barsData.length} smaller sites (<1 GB each) not shown for clarity.`}
        </div>
        <div className="SharePointBar-card" style={{ height: barChartHeight }}>
          <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={isPrintMode ? barChartHeight : "100%"}>
            <BarChart data={barsData} layout="vertical" className="SharePointBar-Chart" margin={{ left: 0, right: 50, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => spaceNumber(v)} domain={[0, xDomainMaxGB]} label={{ value: 'Storage (GB)', position: 'insideBottom', offset: -5, fontSize: 9 }} tick={{ fontSize: 8 }} />
              <YAxis type="category" dataKey="name" width={yAxisWidth} interval={0} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v) => [`${spaceNumber(v)} GB`, 'Storage']} />
              <Bar dataKey="gb" name="GB" radius={[0, 4, 4, 0]} minPointSize={2} isAnimationActive={!isPrintMode}>
                {barsData.map((b, i) => (
                  <Cell key={`bar-gb-${b.name}-${i}`} fill={colorMap.get(b.name) || COLORS[i % COLORS.length]} />
                ))}
                <LabelList dataKey="gb" content={renderGBLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Storage Trend */}
      {activity && rawStorageTrend.length > 0 && (
        <div className="dashboard-card chart-card">
          <h4 className="chart-title" style={{ fontSize: 16 }}>Storage Trend Comparison</h4>
          <div className="chart-subtitle">
            Comparing {prevMonthLabel} (current) vs {priorMonthLabel} (previous) with projected {nextMonthLabel} forecast
          </div>
          <div className="SharePointTrendChart" style={{ height: 220 }}>
            <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={isPrintMode ? 200 : "100%"}>
              <LineChart data={trendChartData} margin={{ left: 0, right: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }} />
                <YAxis tickFormatter={(v) => `${spaceNumber(v, 0)}`} label={{ value: 'Storage (GB)', angle: -90, position: 'insideLeft' }} domain={yDomain} />
                <Tooltip formatter={(v, name) => [`${spaceNumber(v)} GB`, name === 'predictedGB' ? 'Projected' : 'Used']} labelFormatter={(v) => `Day ${v}`} />
                <Legend />
                <Line type="monotone" dataKey="prevUsed" name={`${prevMonthLabel}`} stroke={primaryColor} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} connectNulls />
                <Line type="monotone" dataKey="priorUsed" name={`${priorMonthLabel}`} stroke={secondaryColor} strokeDasharray="6 3" strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} connectNulls />
                <Line type="linear" dataKey="predictedGB" name={`${nextMonthLabel} (Forecast)`} stroke="#26a69a" strokeDasharray="4 4" strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Storage Trend Unavailable Message */}
      {activity && rawStorageTrend.length === 0 && (
        <div className="dashboard-card" style={{ background: '#f8f9fa', borderLeft: '4px solid #ff9800' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 16 }}>Storage Trend Comparison</h4>
          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>
            <p style={{ margin: '0 0 8px 0' }}>
              <strong>Storage trend data is not available for your tenant.</strong>
            </p>
            <p style={{ margin: '0 0 8px 0' }}>
              The Microsoft Graph API endpoint <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 3 }}>getSharePointSiteUsageStorage</code> returned 404 (Not Found), which typically means:
            </p>
            <ul style={{ margin: '4px 0 8px 20px', paddingLeft: 0 }}>
              <li>Your Microsoft 365 subscription type may not include this reporting endpoint</li>
              <li>The endpoint may require specific admin permissions or report reader roles</li>
              <li>Usage reporting may need to be enabled in the Microsoft 365 admin center</li>
            </ul>
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#555' }}>
              <strong>Note:</strong> All other SharePoint features (site usage, activity tracking, collaboration scores) continue to work normally. Only the historical storage trend visualization is affected.
            </p>
          </div>
        </div>
      )}

      {/* Director-Friendly Explanation */}
      <div className="explanation-box">
        <strong>Understanding Your SharePoint Health:</strong>
        <ul>
          <li><strong>Active Sites:</strong> Sites with any viewing or editing activity in {prevMonthLabel}. Higher percentages = better adoption.</li>
          <li><strong>When Sites Were Last Used:</strong> Quick view of how fresh or stale your collaboration areas are. Inactive for 3+ months = review/cleanup candidate.</li>
          <li><strong>Capacity Remaining:</strong> Storage left before hitting tenant limit. Below 20% requires attention.</li>
          <li><strong>Storage Growth:</strong> Month-over-month change. Ideal sustainable growth = 0–5%.</li>
          <li><strong>Collaboration Score:</strong> Composite of adoption, capacity headroom, and healthy growth. 60+ is good; 80+ excellent.</li>
        </ul>
        {criticalAdoptionIssue && (
          <div className="action-callout" style={{ borderLeftColor: '#d32f2f' }}>
            <strong>Critical Issue:</strong> 0% SharePoint usage detected in {prevMonthLabel}. Investigate user adoption and training.
          </div>
        )}
        {limitedData && !criticalAdoptionIssue && (
          <div className="action-callout">
            <strong>Note:</strong> Only {dataWindowDays} days of data so far. Some metrics may show 0% until a full month accumulates.
          </div>
        )}
      </div>
    </div>
  );
};

export default SharepointSiteUsage;